import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import {
  createSupabasePhotoImageUrls,
  serverPhotoObjectPath,
  SIGNED_URL_TTL_SECONDS,
} from '../../data/photos/photo-images';
import type { CapturePhotoView } from '../../data/photos/photo-view';

/**
 * A photo link that outlives a long read — M10 / S10-03B.
 *
 * **THE PROBLEM THIS SOLVES IS SPECIFIC TO A PREVIEW.** Photo links are signed
 * for five minutes and resolved once. On the Capture screen that is right: a
 * photo is glanced at and closed. **A report preview is the opposite** — it is
 * the screen Brandon lingers on, reading his own report the way his client
 * will, before he sends it. Five minutes in, every picture would quietly stop
 * loading, and the failure would look like the report was broken.
 *
 * **THE FIX IS THE SMALLEST ONE THAT IS HONEST.** The link is re-signed shortly
 * before it expires, and again if an image actually fails to load. It is one
 * timer per photo, firing roughly every four minutes — not a poll, and nothing
 * happens at all once the preview is closed.
 *
 * **THE PRIVATE BOUNDARY IS UNCHANGED.** These are the same short-lived signed
 * links, issued to the owner through the same module. Nothing here makes a
 * photo public, lengthens its lifetime, or reaches Storage by any other path.
 */

/** Re-sign at four minutes for a five-minute link: comfortably ahead, never chatty. */
const REFRESH_AFTER_MS = (SIGNED_URL_TTL_SECONDS - 60) * 1000;

export function useReportPhotoUrl(view: CapturePhotoView): {
  readonly url: string | null;
  readonly onError: () => void;
} {
  const client = getSupabaseClient();
  const urls = useMemo(() => (client ? createSupabasePhotoImageUrls(client) : null), [client]);
  const [url, setUrl] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const row = view.row;

  const resolve = useCallback(async (): Promise<string | null> => {
    // **THE DISPLAY DERIVATIVE, NEVER THE ARCHIVE** (PT §9 names report use in
    // as many words). A page of full-resolution originals is how a preview
    // exhausts a phone's memory.
    if (!row || !urls) return null;
    return await urls.signedUrl(serverPhotoObjectPath(row, 'display'));
  }, [row, urls]);

  useEffect(() => {
    let cancelled = false;

    void resolve().then((next) => {
      if (!cancelled) setUrl(next);
    });

    // One timer per photo, and it stops with the screen.
    const timer = setTimeout(() => {
      if (!cancelled) setAttempt((current) => current + 1);
    }, REFRESH_AFTER_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [resolve, attempt]);

  return {
    url,
    // A picture that failed to load is re-signed once rather than left broken:
    // the overwhelmingly likely cause is an expired link.
    onError: useCallback(() => {
      setAttempt((current) => current + 1);
    }, []),
  };
}
